import { useContext, useEffect, useState } from "react";

import { Coding } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import {
  Box,
  Checkbox,
  Grid,
  Group,
  Radio,
  Stack,
  Transition,
  UnstyledButton,
} from "@mantine/core";

import * as Sentry from "@sentry/react";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { isEqual } from "lodash";

import { TextWrapper } from "@/components/TextWrapper";
import { PhysicalExamErrorsContext } from "@/contexts/physicalExamsError";
import { YesNoQuestion } from "@/utils/enums";
import { getValueSetOptions } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

import { PhysicalExamRadioItemProps } from "./types/physical-exams";
import { getCurrentFormValue, getDetailsValues } from "./utils/physical-exams";

const PhysicalExamRadioItem = ({
  name,
  sectionLabel,
  initial,
  url,
  form,
  disabled,
  hideDetails = false,
}: PhysicalExamRadioItemProps) => {
  const [_, section, item] = name.split(".") as [string, string, "singleChoice" | "declined"];
  const sectionData = form.values.sections[section];

  const detailsValues = getDetailsValues(sectionData, item);
  const currentFormValue = getCurrentFormValue(sectionData, item);
  const { addPhysicalExamError, removePhysicalExamError } = useContext(PhysicalExamErrorsContext);

  const [isOpen, setIsOpen] = useState(true);

  const [value, setValue] = useState<boolean>(() => {
    if (currentFormValue !== undefined) {
      return currentFormValue;
    }
    return initial ?? false;
  });
  const [checkboxValues, setCheckboxValues] = useState<string[]>(() => {
    return detailsValues;
  });
  const [options, setOptions] = useState<Coding[]>([]);
  const medplum = useMedplum();

  const listFieldName = `sections.${section}.${item}Details`;

  useEffect(() => {
    (async () => {
      try {
        const options = await getValueSetOptions(medplum, url);
        setOptions(options);
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      }
    })();
  }, [url, medplum]);

  useEffect(() => {
    if (currentFormValue !== undefined && currentFormValue !== value) {
      setValue(currentFormValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFormValue]);

  useEffect(() => {
    if (detailsValues && detailsValues.length !== checkboxValues.length) {
      setCheckboxValues(detailsValues);
    } else if (!isEqual(detailsValues, checkboxValues)) {
      setCheckboxValues(detailsValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsValues]);

  const handleClickProvideMoreDetail = (_e: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(!isOpen);
  };

  const handleRadioChange = (newValue: boolean) => {
    setValue(newValue);
    form.setFieldValue(`sections.${section}.${item}`, newValue);
    const formValidation = form.validate();

    // Check if the current section has validation errors
    const currentSectionHasErrors =
      formValidation.errors &&
      Object.keys(formValidation.errors).some(
        (key) => key.startsWith(`sections.${section}.`) && key.includes("singleChoiceDetails"),
      );

    if (currentSectionHasErrors) {
      addPhysicalExamError(sectionLabel);
    } else {
      removePhysicalExamError(sectionLabel);
    }
  };

  const handleCheckboxChange = (newValues: string[]) => {
    if (value) {
      setCheckboxValues(newValues);
      form.setFieldValue(listFieldName, newValues);
    } else {
      setCheckboxValues([]);
      form.setFieldValue(listFieldName, []);
    }
    const formValidation = form.validate();

    // Check if the current section has validation errors
    const currentSectionHasErrors =
      formValidation.errors &&
      Object.keys(formValidation.errors).some(
        (key) => key.startsWith(`sections.${section}.`) && key.includes("singleChoiceDetails"),
      );

    if (currentSectionHasErrors) {
      addPhysicalExamError(sectionLabel);
    } else {
      removePhysicalExamError(sectionLabel);
    }
  };

  const IconComponent = isOpen ? IconChevronUp : IconChevronDown;

  const showMore = !disabled && !hideDetails && value === true;

  return (
    <Box aria-label={`${sectionLabel} radio`}>
      <Grid mt={"lg"} justify="flex-start">
        <Grid.Col span={12}>
          <Radio.Group
            flex={1}
            withAsterisk
            value={value.toString()}
            styles={{ error: { marginTop: 10 } }}
            error={form.errors[`sections.${section}.${item}`]}
            onChange={(value) => handleRadioChange(value === YesNoQuestion.Yes)}
          >
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <Radio label="No" disabled={disabled} value={YesNoQuestion.No} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Radio label="Yes" disabled={disabled} value={YesNoQuestion.Yes} />
                </Grid.Col>
                {showMore && (
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    <UnstyledButton
                      aria-label="Provide more detail"
                      onClick={handleClickProvideMoreDetail}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Group gap="xs">
                        <TextWrapper variant="bold">Provide more detail</TextWrapper>
                        <TextWrapper c="red.6" variant="default">
                          *
                        </TextWrapper>
                      </Group>
                      <IconComponent
                        size={16}
                        style={{ marginLeft: "var(--mantine-spacing-xs)" }}
                      />
                    </UnstyledButton>
                  </Grid.Col>
                )}
              </Grid>
              <Transition
                duration={200}
                timingFunction="ease"
                transition="fade-down"
                mounted={showMore && isOpen}
              >
                {(style) => (
                  <Grid mt="md" style={style}>
                    <Grid.Col span={{ base: 12, sm: 9 }} offset={{ base: 0, sm: 3 }}>
                      <Checkbox.Group
                        value={checkboxValues}
                        error={form.errors[listFieldName]}
                        styles={{ error: { marginTop: 10 } }}
                        onChange={(e) => handleCheckboxChange(e)}
                      >
                        <Stack gap="lg">
                          {options.map((option) => (
                            <Checkbox
                              key={option.code}
                              value={option.code}
                              label={option.display}
                            />
                          ))}
                        </Stack>
                      </Checkbox.Group>
                    </Grid.Col>
                  </Grid>
                )}
              </Transition>
            </Grid.Col>
          </Radio.Group>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default PhysicalExamRadioItem;
